import { z } from "zod";

/** `NEEDS_HINT`: no guess can arrive on its own — the chat has to ask the user. */
export const IcpStatusSchema = z.enum(["PENDING", "NEEDS_HINT", "READY"]);

export const IcpInfoSchema = z.object({
	status: IcpStatusSchema,
	personas: z.array(z.string()),
	industries: z.array(z.string()),
	size: z.string().nullable(),
	location: z.string().nullable(),
	keywords: z.string().nullable(),
	companyName: z.string().nullable(),
	hasCompanyContext: z.boolean(),
});

export type IcpInfo = z.infer<typeof IcpInfoSchema>;

/** `changed` is false when the model echoed the current ICP back unchanged. */
export const AdjustIcpResponseSchema = IcpInfoSchema.extend({
	changed: z.boolean(),
});

export type AdjustIcpResponse = z.infer<typeof AdjustIcpResponseSchema>;

export const IcpMessageSchema = z.object({
	message: z.string().min(1),
});

export type IcpMessage = z.infer<typeof IcpMessageSchema>;

export const ReferenceCustomerSchema = z.object({
	name: z.string(),
	domain: z.string().nullable(),
	/** What kind of company the extraction read this as; null for a hand-typed chip (PREC-3896). */
	kind: z.string().nullable().optional(),
});

export type ReferenceCustomer = z.infer<typeof ReferenceCustomerSchema>;

export const ReferenceCustomersResponseSchema = z.object({
	customers: z.array(ReferenceCustomerSchema),
	/** The named companies are not all the same kind — they seed one lookalike search (PREC-3896). */
	mixed: z.boolean().optional(),
});

export type ReferenceCustomersResponse = z.infer<typeof ReferenceCustomersResponseSchema>;

/** The edited seed list the chat sends back — names only, resolved server-side. */
export const SetReferenceCustomersSchema = z.object({
	names: z.array(z.string()),
});

export type SetReferenceCustomers = z.infer<typeof SetReferenceCustomersSchema>;

/** The three questions of the pre-checkout priming chat (PREC-3712). */
export const PrimingQuestionSchema = z.enum(["WHEN", "WHY", "WHAT"]);

export type PrimingQuestion = z.infer<typeof PrimingQuestionSchema>;

export const PrimingInfoSchema = z.object({
	status: z.enum(["PENDING", "READY"]),
	drafts: z.object({
		signals: z.array(z.string()),
		valueProp: z.string(),
		research: z.array(z.string()),
	}),
	answers: z.object({
		signals: z.string().nullable(),
		valueProp: z.string().nullable(),
		research: z.string().nullable(),
	}),
});

export type PrimingInfo = z.infer<typeof PrimingInfoSchema>;

export const SubmitPrimingAnswerSchema = z.object({
	question: PrimingQuestionSchema,
	answer: z.string().min(1),
});

export type SubmitPrimingAnswer = z.infer<typeof SubmitPrimingAnswerSchema>;

export const OnboardingListTypeKeySchema = z.enum([
	"hiring",
	"lookalike",
	"leadDb",
	"topic",
	"newInRole",
	"gmaps",
	"aiCompany",
	"aiNews",
	"funding",
	"mna",
]);

/**
 * The filter values the API recommends for the picker. Everything but `types`
 * is a prefill: already validated against the pickers option lists server-side
 * (`list-recommendations.util.ts`), so it can be applied verbatim.
 */
export const ListRecommendationsSchema = z.object({
	types: z.array(OnboardingListTypeKeySchema),
	jobTitles: z.array(z.string()),
	hiringQuery: z.string(),
	topic: z.string(),
	lookalikeDomains: z.array(z.string()),
	seniority: z.array(z.string()),
	departments: z.array(z.string()),
	employeeSizes: z.array(z.string()),
	countries: z.array(z.string()),
	industries: z.array(z.string()),
	keywords: z.array(z.string()),
	fundingRounds: z.array(z.string()),
	fundingCategories: z.array(z.string()),
	businessType: z.string(),
	companyDescription: z.string(),
	newsQuery: z.string(),
});

export type ListRecommendations = z.infer<typeof ListRecommendationsSchema>;
